import React, { useMemo } from "react";
import styled from "styled-components";
import { HiMiniCheckCircle, HiMiniExclamationTriangle } from "react-icons/hi2";

const STORAGE_LABELS = {
  0: "냉장보관",
  1: "상온보관",
  2: "캐리어보관",
};

const LockerNumbers = ({ items, tone }) => (
  <div className={`lockerAssignment ${tone}`}>
    <span className="storageLabel">{STORAGE_LABELS[Number(items[0]?.type)]}</span>
    <div className={`lockerNumbers ${items.length > 1 ? "multiple" : ""}`}>
      {items.map((item, index) => (
        <React.Fragment key={`${item.storageId}-${index}`}>
          {index > 0 && <span className="numberDivider">·</span>}
          <strong>{item.storageId}</strong>
        </React.Fragment>
      ))}
    </div>
  </div>
);

const LockerOpenResultModal = ({
  open,
  storages = [],
  onConfirm,
  action = "store",
}) => {
  const groupedStorages = useMemo(() => {
    const grouped = { 0: [], 1: [], 2: [] };

    storages.forEach((item) => {
      const type = Number(item?.type);
      if (grouped[type]) grouped[type].push(item);
    });

    return grouped;
  }, [storages]);

  if (!open) return null;

  const carrierStorages = groupedStorages[2];
  const cabinetGroups = [groupedStorages[0], groupedStorages[1]].filter(
    (items) => items.length > 0
  );
  const hasCarrier = carrierStorages.length > 0;
  const hasCabinet = cabinetGroups.length > 0;
  const isMixed = hasCarrier && hasCabinet;
  const isRetrieve = action === "retrieve";

  return (
    <Overlay role="presentation">
      <Dialog
        role="dialog"
        aria-modal="true"
        aria-labelledby="locker-open-result-title"
        className={isMixed ? "mixed" : "single"}
      >
        <div className="resultScroll">
          {hasCarrier && (
            <GuideSection className="carrierSection">
              <div className="sectionHeading">
                <span className="statusIcon warning" aria-hidden="true">
                  <HiMiniExclamationTriangle />
                </span>
                <h2 id="locker-open-result-title">캐리어 문이 열렸어요!</h2>
              </div>

              <LockerNumbers items={carrierStorages} tone="carrier" />

              <div className="direction carrierDirection">
                <span aria-hidden="true">⬅</span> 왼쪽으로 쭉!
              </div>

              <div className="locationImage">
                <img
                  src="/img/selfService/carrier-locker-guide.png"
                  alt="매장 왼쪽 캐리어 보관함 위치"
                />
              </div>

              <div className="carrierWarning">
                <strong>15초</strong>
                <span>
                  안에 문이 닫혀요.
                  <br />
                  {isRetrieve ? "바로 꺼내주세요!" : "바로 넣어주세요!"}
                </span>
              </div>
            </GuideSection>
          )}

          {hasCabinet && (
            <GuideSection className="cabinetSection">
              <div className="sectionHeading">
                <span className="statusIcon success" aria-hidden="true">
                  <HiMiniCheckCircle />
                </span>
                <h2 id={hasCarrier ? undefined : "locker-open-result-title"}>
                  캐비닛 문이 열렸습니다!
                </h2>
              </div>

              <div className="cabinetAssignments">
                {cabinetGroups.map((items) => (
                  <LockerNumbers
                    key={Number(items[0]?.type)}
                    items={items}
                    tone="cabinet"
                  />
                ))}
              </div>

              <div className="direction cabinetDirection">
                오른쪽 유리문 안쪽입니다 <span aria-hidden="true">➡</span>
              </div>

              <div className="locationImage">
                <img
                  src="/img/selfService/cold-room-locker-guide.png"
                  alt="매장 오른쪽 유리문 안쪽 냉장 및 상온 보관함 위치"
                />
              </div>

              <div className="cabinetInstruction">
                {isRetrieve ? "안쪽으로 들어가 꺼내주세요" : "안쪽으로 들어가주세요"}
              </div>
            </GuideSection>
          )}
        </div>

        <button type="button" className="confirmButton" onClick={onConfirm} autoFocus>
          확인
        </button>
      </Dialog>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  background: rgba(15, 23, 42, 0.78);
  backdrop-filter: blur(7px);
`;

const Dialog = styled.div`
  width: min(920px, 94vw);
  max-height: 94vh;
  padding: 22px;
  box-sizing: border-box;
  border-radius: 34px;
  background: #ffffff;
  box-shadow: 0 34px 100px rgba(15, 23, 42, 0.38);
  display: flex;
  flex-direction: column;
  gap: 18px;
  overflow: hidden;

  .resultScroll {
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 2px;
  }

  .confirmButton {
    width: 100%;
    min-height: 82px;
    flex: 0 0 82px;
    border: none;
    border-radius: 22px;
    background: linear-gradient(135deg, #3187ef 0%, #176ed4 100%);
    color: #ffffff;
    font-size: 32px;
    font-weight: 1000;
    box-shadow: 0 14px 30px rgba(23, 110, 212, 0.28);
    cursor: pointer;
  }

  &.single .resultScroll {
    justify-content: center;
  }

  &.single .locationImage {
    aspect-ratio: auto;
  }

  &.single .locationImage img {
    height: auto;
    max-height: 54vh;
    object-fit: contain;
  }

  @media screen and (max-width: 767px) {
    width: 96vw;
    max-height: 96vh;
    padding: 12px;
    border-radius: 24px;
    gap: 10px;

    .confirmButton {
      min-height: 58px;
      flex-basis: 58px;
      border-radius: 16px;
      font-size: 22px;
    }
  }
`;

const GuideSection = styled.section`
  border: 2px solid rgba(15, 23, 42, 0.08);
  border-radius: 28px;
  padding: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  text-align: center;

  &.carrierSection {
    border-color: rgba(239, 68, 68, 0.2);
    background: linear-gradient(180deg, #fffdfd 0%, #fff7f7 100%);
  }

  .sectionHeading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 12px;
  }

  .sectionHeading h2 {
    margin: 0;
    color: #111827;
    font-size: clamp(28px, 3vw, 42px);
    line-height: 1.15;
    font-weight: 1000;
    letter-spacing: -0.04em;
    word-break: keep-all;
  }

  &.carrierSection .sectionHeading h2 {
    color: #ef1919;
  }

  .statusIcon {
    width: 56px;
    height: 56px;
    flex: 0 0 56px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 38px;
  }

  .statusIcon.warning {
    background: #ff9500;
  }

  .statusIcon.success {
    background: #22c55e;
  }

  .lockerAssignment {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
  }

  .cabinetAssignments {
    display: flex;
    align-items: stretch;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .cabinetAssignments .lockerAssignment {
    min-width: 220px;
    flex: 1 1 220px;
  }

  .storageLabel {
    min-height: 42px;
    padding: 0 24px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 21px;
    font-weight: 950;
  }

  .lockerAssignment.carrier .storageLabel {
    background: #ff5a16;
  }

  .lockerAssignment.cabinet .storageLabel {
    background: #3187ef;
  }

  .lockerNumbers {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    color: #111111;
    font-size: clamp(58px, 8vw, 94px);
    line-height: 0.95;
    letter-spacing: -0.06em;
  }

  .lockerNumbers.multiple {
    font-size: clamp(42px, 5.5vw, 68px);
  }

  .lockerNumbers strong {
    font-weight: 1000;
  }

  .numberDivider {
    margin: 0 10px;
    color: #94a3b8;
    font-size: 0.7em;
  }

  .direction {
    margin: 12px 0;
    font-size: clamp(25px, 3vw, 40px);
    line-height: 1.15;
    font-weight: 1000;
    letter-spacing: -0.04em;
    word-break: keep-all;
  }

  .carrierDirection {
    color: #111827;
  }

  .carrierDirection span {
    color: #ef1919;
  }

  .cabinetDirection {
    color: #176ed4;
  }

  .locationImage {
    width: 100%;
    aspect-ratio: 16 / 8;
    border-radius: 20px;
    overflow: hidden;
    background: #e2e8f0;
    box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.08);
  }

  .locationImage img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  .carrierWarning {
    margin-top: 14px;
    min-height: 82px;
    padding: 8px 18px;
    border-radius: 20px;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    color: #ef1919;
  }

  .carrierWarning strong {
    font-size: clamp(58px, 7vw, 86px);
    line-height: 0.95;
    font-weight: 1000;
    letter-spacing: -0.07em;
  }

  .carrierWarning span {
    font-size: clamp(22px, 2.5vw, 32px);
    line-height: 1.2;
    font-weight: 1000;
    text-align: left;
    word-break: keep-all;
  }

  .cabinetInstruction {
    margin-top: 14px;
    color: #111827;
    font-size: clamp(30px, 4vw, 54px);
    line-height: 1.1;
    font-weight: 1000;
    letter-spacing: -0.05em;
  }

  @media screen and (max-width: 767px) {
    padding: 12px;
    border-radius: 20px;

    .sectionHeading {
      gap: 8px;
      margin-bottom: 8px;
    }

    .sectionHeading h2 {
      font-size: 22px;
    }

    .statusIcon {
      width: 38px;
      height: 38px;
      flex-basis: 38px;
      font-size: 26px;
    }

    .storageLabel {
      min-height: 30px;
      padding: 0 14px;
      font-size: 14px;
    }

    .lockerNumbers,
    .lockerNumbers.multiple {
      font-size: 40px;
    }

    .direction {
      margin: 8px 0;
      font-size: 20px;
    }

    .locationImage {
      border-radius: 14px;
    }

    .carrierWarning {
      min-height: 58px;
      margin-top: 8px;
      padding: 6px 12px;
      gap: 12px;
    }

    .carrierWarning strong {
      font-size: 42px;
    }

    .carrierWarning span {
      font-size: 17px;
    }

    .cabinetInstruction {
      margin-top: 8px;
      font-size: 26px;
    }
  }
`;

export default LockerOpenResultModal;
